// frontend/src/components/QuestionItem.js

import React, { memo } from "react";
import PropTypes from "prop-types";
import CommentsSection from "./CommentsSection";
import "../pages/SurveyDetails.css";

function QuestionItem({
  question,
  index,
  highlighted,
  isSortingMode,
  showComments,
  comments,
  commentsLoading,
  handleAddComment,
  markNotificationsAsRead,
  handleEditQuestion,
  handleRemixQuestion,
  handleDeleteQuestion,
  userName,
  onToggleComments,
}) {
  const handleCommentsClick = () => {
    onToggleComments(question._id);
    if (highlighted) {
      markNotificationsAsRead(question._id);
    }
  };

  return (
    <div
      className={`survey-question-container 
        ${highlighted ? "highlighted" : ""} 
        ${isSortingMode ? "sorting-mode" : ""}`}
    >
      {highlighted && (
        <span className="highlight-badge" aria-label="New Comments" title="New Comments">
          🟡
        </span>
      )}

      {/* Question Text & Type */}
      <div className="question-header">
        <p className="question-text">
          <strong>Q{question.number}:</strong> {question.questionText}
        </p>
        <p className="question-type">Type: {question.questionType}</p>
      </div>

      {/* Answer Options List */}
      {question.answerOptions?.length ? (
        <ul className="answer-options">
          {question.answerOptions.map((opt, idx) => {
            const optionText = typeof opt === "string" ? opt : opt.optionText;
            const showQ =
              question.isQualifyingQuestion &&
              typeof opt === "object" &&
              opt.isQualify;
            return (
              <li key={idx}>
                {optionText}
                {showQ && (
                  <strong style={{ color: "#4535bf", marginLeft: "5px" }}>
                    (Q)
                  </strong>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="no-answer-options"></p>
      )}

      {/* Qualifying question label */}
      {question.isQualifyingQuestion && (
        <div className="qualifying-question-label">
          <i className="fa-sharp fa-regular fa-filter-list" style={{ marginRight: "5px" }} />
          Qualifying question
        </div>
      )}

      {/* Comments section */}
      {showComments && (
        <CommentsSection
          questionId={question._id}
          comments={comments}
          onAddComment={handleAddComment}
          reviewerName={userName}
          markNotificationsAsRead={markNotificationsAsRead}
        />
      )}

      {/* Conditionally render the action buttons only if at least one function is provided */}
      {(handleEditQuestion || handleRemixQuestion || handleDeleteQuestion) && (
        <div className="question-actions">
          <button
            onClick={handleCommentsClick}
            className={`comment-button tooltip ${highlighted ? "unread-comment-button" : ""}`}
            aria-label="View Comments"
          >
            <i className="fa-regular fa-message-dots"></i>
            <span className="tooltiptext">Comments</span>
          </button>
          {handleEditQuestion && (
            <button
              onClick={() => handleEditQuestion(question)}
              className="edit-button tooltip"
              aria-label="Edit Question"
            >
              <i className="fa-solid fa-edit"></i>
              <span className="tooltiptext">Edit</span>
            </button>
          )}
          {handleRemixQuestion && (
            <button
              onClick={() => handleRemixQuestion(question)}
              className="remix-button tooltip"
              aria-label="Remix Question"
            >
              <i className="fa-regular fa-shuffle"></i>
              <span className="tooltiptext">Remix</span>
            </button>
          )}
          {handleDeleteQuestion && (
            <button
              onClick={() => handleDeleteQuestion(question._id)}
              className="delete-button tooltip"
              aria-label="Delete Question"
            >
              <i className="fa-solid fa-trash"></i>
              <span className="tooltiptext">Delete</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

QuestionItem.propTypes = {
  question: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    number: PropTypes.number.isRequired,
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.string.isRequired,
    // answerOptions can be an array of objects or strings.
    answerOptions: PropTypes.array.isRequired,
    isQualifyingQuestion: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  highlighted: PropTypes.bool.isRequired,
  isSortingMode: PropTypes.bool.isRequired,
  showComments: PropTypes.bool.isRequired,
  comments: PropTypes.array.isRequired,
  commentsLoading: PropTypes.bool.isRequired,
  handleAddComment: PropTypes.func.isRequired,
  markNotificationsAsRead: PropTypes.func.isRequired,
  handleEditQuestion: PropTypes.func,
  handleRemixQuestion: PropTypes.func,
  handleDeleteQuestion: PropTypes.func,
  userName: PropTypes.string.isRequired,
  onToggleComments: PropTypes.func.isRequired,
};

export default memo(QuestionItem);
